/**
 * Oendies cart drawer behavior (no theme imports).
 * Fixes:
 * - add-to-cart redirect -> open drawer
 * - quantity +/- and remove should work on mobile+desktop without page refresh
 */

const OENDIES_CART = {
  delegatesBound: false,
  activeChangeAbort: null,
  activeRefreshAbort: null,
  loadingStyleInjected: false,
};

/**
 * @param {string} url
 * @param {RequestInit} [options]
 */
async function fetchJson(url, options) {
  const res = await fetch(url, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...(options && options.headers ? options.headers : {}),
    },
    ...options,
  });

  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (_) {
    data = null;
  }

  if (!res.ok) {
    const err = new Error('Request failed');
    // @ts-ignore
    err.response = res;
    // @ts-ignore
    err.data = data;
    throw err;
  }

  return data;
}

/**
 * @param {string} pathname
 * @returns {string}
 */
function getLocalePrefixFromPath(pathname) {
  const path = typeof pathname === 'string' ? pathname.toLowerCase() : '';
  const m = path.match(/^\/(da|de|es|fi|fr|it|nl|pl|pt|pt-pt|sv)(?=\/|$)/i);
  if (!m) return '';

  const locale = (m[1] || '').toLowerCase();
  if (locale === 'nl') return '';
  if (locale === 'pt-pt') return '/pt';
  return `/${locale}`;
}

/**
 * @param {string} path
 * @returns {string}
 */
function withLocalePrefix(path) {
  if (typeof path !== 'string' || !path.startsWith('/')) return path;
  const prefix = getLocalePrefixFromPath(window.location.pathname || '');
  if (!prefix) return path;
  if (path === prefix || path.startsWith(`${prefix}/`)) return path;
  return `${prefix}${path}`;
}

/**
 * Keep section rendering bound to the current locale path.
 * Shopify returns empty HTML for some section-group IDs on product URLs,
 * so we always render from the locale root.
 * @returns {string}
 */
function getLocaleRootPath() {
  const prefix = getLocalePrefixFromPath(window.location.pathname || '');
  return prefix || '/';
}

/**
 * Build section rendering URL.
 * @param {string} sectionId
 * @returns {string}
 */
function buildSectionRenderUrl(sectionId) {
  const u = new URL(window.location.origin);
  u.pathname = getLocaleRootPath();
  u.searchParams.set('sections', sectionId);
  u.searchParams.set('_', String(Date.now()));
  return `${u.pathname}${u.search}`;
}

/**
 * @param {HTMLElement} el
 */
function resolveSectionId(el) {
  const cid = el.querySelector('cart-items-component')?.dataset?.sectionId;
  if (typeof cid === 'string' && cid) return cid;
  const section = el.closest('[id^="shopify-section-"]');
  if (section instanceof HTMLElement && typeof section.id === 'string') {
    return section.id.replace('shopify-section-', '') || null;
  }
  return null;
}

/**
 * @param {HTMLElement} el
 * @returns {number | null}
 */
function resolveCartLine(el) {
  // data-line
  const dataHost = el.closest('[data-line]');
  if (dataHost instanceof HTMLElement) {
    const v = parseInt(dataHost.getAttribute('data-line') || '', 10);
    if (Number.isFinite(v) && v > 0) return v;
  }

  // on:click="/onLineItemRemove/X"
  const onClickHost = el.closest('[on\\:click]');
  if (onClickHost instanceof HTMLElement) {
    const attr = onClickHost.getAttribute('on:click') || '';
    const m = attr.match(/\/onLineItemRemove\/(\d+)/);
    if (m) {
      const v = parseInt(m[1], 10);
      if (Number.isFinite(v) && v > 0) return v;
    }
  }

  // row index fallback
  const row = el.closest('tr.cart-items__table-row');
  if (!(row instanceof HTMLTableRowElement)) return null;
  const tbody = row.closest('tbody');
  const rows = tbody
    ? Array.from(tbody.querySelectorAll('tr.cart-items__table-row'))
    : Array.from(row.parentElement?.querySelectorAll('tr.cart-items__table-row') || []);
  const idx = rows.indexOf(row);
  if (idx !== -1) return idx + 1;
  return null;
}

/**
 * Shadow-DOM-safe line resolver using an event composedPath.
 * @param {Array<unknown>} path
 * @returns {number | null}
 */
function resolveCartLineFromPath(path) {
  for (const node of path) {
    if (!(node instanceof HTMLElement)) continue;
    if (!node.hasAttribute('data-line')) continue;
    const v = parseInt(node.getAttribute('data-line') || '', 10);
    if (Number.isFinite(v) && v > 0) return v;
  }

  for (const node of path) {
    if (!(node instanceof HTMLElement)) continue;
    const attr = node.getAttribute('on:click') || '';
    const m = attr.match(/\/onLineItemRemove\/(\d+)/);
    if (!m) continue;
    const v = parseInt(m[1], 10);
    if (Number.isFinite(v) && v > 0) return v;
  }

  const row = path.find(
    (n) => n instanceof HTMLTableRowElement && n.matches('tr.cart-items__table-row')
  );
  if (!(row instanceof HTMLTableRowElement)) return null;

  const tbody = row.closest('tbody');
  const rows = tbody
    ? Array.from(tbody.querySelectorAll('tr.cart-items__table-row'))
    : Array.from(row.parentElement?.querySelectorAll('tr.cart-items__table-row') || []);
  const idx = rows.indexOf(row);
  if (idx !== -1) return idx + 1;
  return null;
}

/**
 * @param {Event} e
 */
function getComposedPath(e) {
  // @ts-ignore
  return typeof e.composedPath === 'function' ? e.composedPath() : [];
}

/**
 * @param {Array<unknown>} path
 * @param {string} selector
 */
function firstMatchInPath(path, selector) {
  for (const node of path) {
    if (!(node instanceof HTMLElement)) continue;
    if (node.matches(selector)) return node;
  }
  return null;
}

/**
 * @param {HTMLElement} drawer
 */
function getDialog(drawer) {
  const d = drawer.querySelector('dialog');
  return d instanceof HTMLDialogElement ? d : null;
}

/**
 * @param {HTMLElement} drawer
 */
function isDrawerOpen(drawer) {
  const d = getDialog(drawer);
  return !!(d && d.open);
}

/**
 * @param {HTMLElement} drawer
 */
function openDrawer(drawer) {
  const d = getDialog(drawer);
  if (!d) return;
  if (typeof d.showModal === 'function') {
    if (!d.open) d.showModal();
  } else {
    d.setAttribute('open', '');
  }
  document.documentElement.style.overflow = 'hidden';

  // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
  customElements.whenDefined('shopify-payment-terms').then(() => {
    const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
    const cta = installmentsContent?.querySelector('#shopify-installments-cta');
    cta?.addEventListener(
      'click',
      () => {
        closeDrawer(drawer);
      },
      { once: true }
    );
  });
}

/**
 * @param {HTMLElement} drawer
 */
function closeDrawer(drawer) {
  const d = getDialog(drawer);
  if (!d) return;
  if (typeof d.close === 'function' && d.open) d.close();
  else d.removeAttribute('open');
  document.documentElement.style.overflow = '';
}

function ensureLoadingStyles() {
  if (OENDIES_CART.loadingStyleInjected) return;
  if (document.getElementById('oendies-cart-drawer-loading-style')) {
    OENDIES_CART.loadingStyleInjected = true;
    return;
  }

  const style = document.createElement('style');
  style.id = 'oendies-cart-drawer-loading-style';
  style.textContent = `
    cart-drawer-component[data-loading="true"] .cart-drawer__dialog {
      overflow: hidden;
    }

    cart-drawer-component[data-loading="true"] .cart-drawer__inner {
      visibility: hidden;
    }

    cart-drawer-component[data-loading="true"] .cart-drawer__dialog::before {
      content: "";
      position: absolute;
      inset: 0;
      background: rgba(255, 255, 255, 0.88);
      z-index: 3;
    }

    cart-drawer-component[data-loading="true"] .cart-drawer__dialog::after {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 30px;
      height: 30px;
      margin-top: -15px;
      margin-left: -15px;
      border: 3px solid rgba(34, 67, 42, 0.2);
      border-top-color: #22432A;
      border-radius: 50%;
      animation: oendies-cart-drawer-spin 0.75s linear infinite;
      z-index: 4;
    }

    @keyframes oendies-cart-drawer-spin {
      to {
        transform: rotate(360deg);
      }
    }
  `;
  document.head.appendChild(style);
  OENDIES_CART.loadingStyleInjected = true;
}

/**
 * @param {HTMLElement} drawer
 * @param {boolean} busy
 */
function setDrawerBusy(drawer, busy) {
  ensureLoadingStyles();
  const d = getDialog(drawer);
  if (busy) {
    drawer.setAttribute('data-loading', 'true');
    if (d) d.setAttribute('aria-busy', 'true');
  } else {
    drawer.removeAttribute('data-loading');
    if (d) d.removeAttribute('aria-busy');
  }
}

/**
 * Render a fresh cart drawer HTML payload into the active drawer.
 * @param {HTMLElement} drawer
 * @param {string} html
 * @param {boolean} shouldOpen
 * @returns {boolean}
 */
function applyDrawerSectionHtml(drawer, html, shouldOpen) {
  if (typeof html !== 'string' || !html.trim()) return false;

  const items = drawer.querySelector('.cart-drawer__items');
  const prevScrollTop = items instanceof HTMLElement ? items.scrollTop : 0;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const nextDrawer = doc.querySelector('cart-drawer-component');
  if (!(nextDrawer instanceof HTMLElement)) return false;

  const currentDialog = getDialog(drawer);
  const nextDialog = getDialog(nextDrawer);

  // Update cart icon button content/count
  const currentToggle = drawer.querySelector('[on\\:click="/open"]');
  const nextToggle = nextDrawer.querySelector('[on\\:click="/open"]');
  if (currentToggle instanceof HTMLElement && nextToggle instanceof HTMLElement) {
    currentToggle.replaceWith(nextToggle.cloneNode(true));
  }

  // Update dialog inner content without replacing the dialog element
  if (currentDialog && nextDialog) {
    currentDialog.className = nextDialog.className;
    const curInner = currentDialog.querySelector('.cart-drawer__inner');
    const nxtInner = nextDialog.querySelector('.cart-drawer__inner');
    if (curInner instanceof HTMLElement && nxtInner instanceof HTMLElement) {
      curInner.innerHTML = nxtInner.innerHTML;
    }
  } else {
    drawer.innerHTML = nextDrawer.innerHTML;
  }

  // Restore scroll
  const newItems = drawer.querySelector('.cart-drawer__items');
  if (newItems instanceof HTMLElement) newItems.scrollTop = prevScrollTop;

  if (shouldOpen) openDrawer(drawer);
  return true;
}

/**
 * @param {HTMLElement} drawer
 */
async function refreshDrawer(drawer) {
  const sectionId = resolveSectionId(drawer);
  if (!sectionId) return;

  const shouldOpen = isDrawerOpen(drawer);

  // Abort previous refresh
  if (OENDIES_CART.activeRefreshAbort) OENDIES_CART.activeRefreshAbort.abort();
  OENDIES_CART.activeRefreshAbort = new AbortController();

  try {
    setDrawerBusy(drawer, true);
    const data = await fetchJson(buildSectionRenderUrl(sectionId), {
      method: 'GET',
      cache: 'no-store',
      signal: OENDIES_CART.activeRefreshAbort.signal,
    });

    const html = data?.[sectionId];
    applyDrawerSectionHtml(drawer, html, shouldOpen);
  } catch (_) {
    // ignore
  } finally {
    setDrawerBusy(drawer, false);
    OENDIES_CART.activeRefreshAbort = null;
  }
}

/**
 * @param {HTMLElement} drawer
 * @param {number} line
 * @param {number} quantity
 */
async function changeLine(drawer, line, quantity) {
  if (!Number.isFinite(line) || line <= 0) return;
  if (!Number.isFinite(quantity) || quantity < 0) return;

  if (OENDIES_CART.activeChangeAbort) OENDIES_CART.activeChangeAbort.abort();
  OENDIES_CART.activeChangeAbort = new AbortController();

  try {
    const body = new URLSearchParams();
    body.set('line', String(line));
    body.set('quantity', String(quantity));

    await fetchJson(withLocalePrefix('/cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body,
      cache: 'no-store',
      signal: OENDIES_CART.activeChangeAbort.signal,
    });
    await refreshDrawer(drawer);
  } catch (_) {
    // ignore
  } finally {
    OENDIES_CART.activeChangeAbort = null;
  }
}

/**
 * @param {number} line
 * @returns {Promise<number | null>}
 */
async function getQuantityForLine(line) {
  if (!Number.isFinite(line) || line <= 0) return null;
  try {
    const cartPath = withLocalePrefix('/cart.js');
    const cart = await fetchJson(`${cartPath}?_=${Date.now()}`, { method: 'GET', cache: 'no-store' });
    const item = cart?.items?.[line - 1];
    const q = item?.quantity;
    return Number.isFinite(q) ? q : null;
  } catch (_) {
    return null;
  }
}

/**
 * @param {HTMLElement} el
 * @returns {number | null}
 */
function parseQtyFromInput(el) {
  if (!(el instanceof HTMLInputElement)) return null;
  const v = parseInt(el.value, 10);
  return Number.isFinite(v) ? v : null;
}

/**
 * @param {HTMLElement} trigger
 * @returns {-1 | 0 | 1}
 */
function detectQtyDelta(trigger) {
  const label = (trigger.getAttribute('aria-label') || '').toLowerCase();
  const text = (trigger.textContent || '').trim();
  const name = (trigger.getAttribute('name') || '').toLowerCase();
  const action = (trigger.getAttribute('data-action') || '').toLowerCase();
  const cls = (trigger.getAttribute('class') || '').toLowerCase();

  const minusHints = ['minus', 'decrease', 'verminder', 'min', 'down'];
  const plusHints = ['plus', 'increase', 'verhoog', 'add', 'up'];

  const looksMinus =
    text === '-' ||
    text === '−' ||
    action === 'minus' ||
    name === 'minus' ||
    minusHints.some((h) => label.includes(h) || cls.includes(h));

  const looksPlus =
    text === '+' ||
    action === 'plus' ||
    name === 'plus' ||
    plusHints.some((h) => label.includes(h) || cls.includes(h));

  if (looksMinus && !looksPlus) return -1;
  if (looksPlus && !looksMinus) return 1;
  return 0;
}

function bindDelegates() {
  if (OENDIES_CART.delegatesBound) return;
  OENDIES_CART.delegatesBound = true;

  // Drawer open/close + remove + qty buttons
  document.addEventListener(
    'click',
    async (e) => {
      const path = getComposedPath(e);
      const drawer = firstMatchInPath(path, 'cart-drawer-component');
      if (!(drawer instanceof HTMLElement)) return;

      const openBtn = firstMatchInPath(path, '[on\\:click="/open"]');
      if (openBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        openDrawer(drawer);
        return;
      }

      const closeBtn = firstMatchInPath(path, '.cart-drawer__close-button, [on\\:click*="/close"]');
      if (closeBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        closeDrawer(drawer);
        return;
      }

      // Clicking the modal backdrop (outside the side panel) should close the drawer.
      const dialog = getDialog(drawer);
      const clickedNode = path.length ? path[0] : null;
      if (dialog && dialog.open && clickedNode === dialog) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        closeDrawer(drawer);
        return;
      }

      const removeBtn = firstMatchInPath(path, '.cart-items__remove, [on\\:click^="/onLineItemRemove/"]');
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
        const line = resolveCartLineFromPath(path) || resolveCartLine(removeBtn);
        if (!line) return;
        changeLine(drawer, line, 0);
        return;
      }

      const qtyWrap = firstMatchInPath(path, '.quantity-selector, quantity-selector, .quantity');
      if (qtyWrap) {
        const wrapIndex = path.indexOf(qtyWrap);
        const subPath = wrapIndex === -1 ? path : path.slice(0, wrapIndex);

        const trigger =
          subPath.find(
            (n) =>
              n instanceof HTMLElement &&
              (n.matches('button, [role="button"], a') ||
                n.hasAttribute('data-action') ||
                n.hasAttribute('aria-label') ||
                n.hasAttribute('name'))
          ) || null;

        if (!(trigger instanceof HTMLElement)) return;

        let delta = detectQtyDelta(trigger);
        if (delta === 0 && qtyWrap instanceof HTMLElement) {
          // fallback: compare to first/last clickable within wrap (light DOM only)
          const btns = Array.from(qtyWrap.querySelectorAll('button, [role="button"], a'));
          if (btns.length >= 2) {
            if (trigger === btns[0]) delta = -1;
            if (trigger === btns[btns.length - 1]) delta = 1;
          }
        }
        if (delta === 0) return;

        const line = resolveCartLineFromPath(path) || resolveCartLine(qtyWrap);
        if (!line) return;

        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

        const inputFromPath = subPath.find((n) => n instanceof HTMLInputElement);
        const inputFromWrap =
          qtyWrap instanceof HTMLElement ? qtyWrap.querySelector('input[type="number"], input') : null;
        const input =
          inputFromPath instanceof HTMLInputElement
            ? inputFromPath
            : inputFromWrap instanceof HTMLInputElement
              ? inputFromWrap
              : null;

        let current = input ? parseQtyFromInput(input) : null;
        if (current == null) current = await getQuantityForLine(line);
        if (current == null) return;

        const next = Math.max(0, current + delta);
        if (input) input.value = String(next);
        changeLine(drawer, line, next);
      }
    },
    true
  );

  // Quantity input change (no submit)
  document.addEventListener(
    'change',
    (e) => {
      const path = getComposedPath(e);
      const input = path.find((n) => n instanceof HTMLInputElement);
      if (!input) return;

      const drawer = firstMatchInPath(path, 'cart-drawer-component');
      if (!(drawer instanceof HTMLElement)) return;

      const isQty = !!firstMatchInPath(path, '.cart-items__quantity');
      if (!isQty) return;

      const line = resolveCartLineFromPath(path) || resolveCartLine(input);
      if (!line) return;

      const next = Math.max(0, parseInt(input.value, 10) || 0);
      input.value = String(next);
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      changeLine(drawer, line, next);
    },
    true
  );

  // Prevent cart form submit inside drawer (except checkout)
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target instanceof HTMLFormElement ? e.target : null;
      if (!form) return;

      const drawer = form.closest('cart-drawer-component');
      if (!(drawer instanceof HTMLElement)) return;

      const submitter = e.submitter instanceof HTMLElement ? e.submitter : null;
      const activeEl = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      const isCheckout =
        submitter?.getAttribute('name') === 'checkout' ||
        submitter?.id === 'checkout' ||
        submitter?.matches?.('[name="checkout"], #checkout') ||
        activeEl?.matches?.('[name="checkout"], #checkout, .cart__checkout-button');
      if (isCheckout) return;

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
    },
    true
  );

  // Intercept add-to-cart submits globally
  document.addEventListener(
    'submit',
    async (e) => {
      const form = e.target instanceof HTMLFormElement ? e.target : null;
      if (!form) return;

      const action = (form.getAttribute('action') || form.action || '').toLowerCase();
      if (!action.includes('/cart/add')) return;

      const drawer = document.querySelector('cart-drawer-component');
      if (!(drawer instanceof HTMLElement)) return;
      const sectionId = resolveSectionId(drawer);
      if (!sectionId) return;

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

      try {
        const shouldOpen = drawer.hasAttribute('auto-open') || isDrawerOpen(drawer);
        if (shouldOpen) {
          openDrawer(drawer);
          setDrawerBusy(drawer, true);
        }

        const payload = new FormData(form);
        payload.set('sections', sectionId);
        payload.set('sections_url', getLocaleRootPath());

        const response = await fetchJson(withLocalePrefix('/cart/add.js'), {
          method: 'POST',
          body: payload,
        });

        const html = response?.sections?.[sectionId];
        const didApply = applyDrawerSectionHtml(drawer, html, shouldOpen);
        if (!didApply) {
          await refreshDrawer(drawer);
          if (drawer.hasAttribute('auto-open')) openDrawer(drawer);
        }
      } catch (_) {
        // ignore
      } finally {
        setDrawerBusy(drawer, false);
      }
    },
    true
  );
}

bindDelegates();

// Define element only if missing (delegates already handle behavior)
if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    class extends HTMLElement {
      connectedCallback() {
        // no-op
      }
    }
  );
}
